/**
 * Export Analysis Button Component
 *
 * A reusable button that generates and exports a recommendation analysis
 * as a PDF or shareable image.
 *
 * Features:
 * - PDF generation using html2canvas + jspdf
 * - Image export with Web Share API fallback
 * - Download fallback for non-shareable browsers
 * - Toast notifications for progress and status
 * - Visual feedback during export operation
 */

import * as React from "react";
import { Download, FileText, Loader2, Image } from "lucide-react";
import { Button, type buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { VariantProps } from "class-variance-authority";
import { useExportAnalysis, type ExportFormat } from "@/hooks/useExportAnalysis";

// ============================================================================
// Types
// ============================================================================

export type { ExportFormat };

export interface ExportAnalysisButtonProps
  extends Omit<React.ComponentProps<"button">, "onClick">,
    VariantProps<typeof buttonVariants> {
  /** The element ref to capture for export */
  targetRef: React.RefObject<HTMLElement | null>;
  /** Fund or recommendation name for the filename */
  fileName?: string;
  /** Optional label to show alongside the icon */
  showLabel?: boolean;
  /** Custom label text (default: "Export Analysis") */
  label?: string;
  /** Callback fired after successful export */
  onExportSuccess?: (format: ExportFormat) => void;
  /** Callback fired after export failure */
  onExportError?: (error: Error) => void;
  /** Whether to show format dropdown or export directly */
  showFormatOptions?: boolean;
  /** Default format when not showing dropdown */
  defaultFormat?: ExportFormat;
  /** Custom title for the exported document */
  exportTitle?: string;
  /** Custom subtitle for the exported document */
  exportSubtitle?: string;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_FILE_NAME = "recommendation-analysis";
const DEFAULT_LABEL = "Export Analysis";

// ============================================================================
// Main Component
// ============================================================================

/**
 * ExportAnalysisButton
 *
 * A button component that exports a recommendation analysis as PDF or image.
 *
 * @example
 * ```tsx
 * function RecommendationDetail() {
 *   const contentRef = useRef<HTMLDivElement>(null);
 *
 *   return (
 *     <div>
 *       <ExportAnalysisButton
 *         targetRef={contentRef}
 *         fileName="axis-small-cap-analysis"
 *         exportTitle="Switch Recommendation: Axis Small Cap"
 *       />
 *       <div ref={contentRef}>
 *         {/* Content to export *\/}
 *       </div>
 *     </div>
 *   );
 * }
 * ```
 */
export function ExportAnalysisButton({
  targetRef,
  fileName = DEFAULT_FILE_NAME,
  showLabel = false,
  label = DEFAULT_LABEL,
  variant = "outline",
  size = "sm",
  className,
  onExportSuccess,
  onExportError,
  showFormatOptions = true,
  defaultFormat = "pdf",
  exportTitle,
  exportSubtitle,
  disabled,
  ...props
}: ExportAnalysisButtonProps) {
  const { exportTo, isExporting, exportingFormat, canShareImages } =
    useExportAnalysis(targetRef);

  const handleExport = React.useCallback(
    async (format: ExportFormat) => {
      try {
        await exportTo(format, {
          fileName,
          title: exportTitle,
          subtitle: exportSubtitle,
        });
        onExportSuccess?.(format);
      } catch (error) {
        const err = error instanceof Error ? error : new Error("Export failed");
        onExportError?.(err);
      }
    },
    [exportTo, fileName, exportTitle, exportSubtitle, onExportSuccess, onExportError]
  );

  // Direct export without format options
  if (!showFormatOptions) {
    return (
      <Button
        type="button"
        variant={variant}
        size={size}
        className={cn("gap-2", className)}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleExport(defaultFormat);
        }}
        disabled={disabled || isExporting || !targetRef.current}
        aria-label={label}
        title={label}
        {...props}
      >
        {isExporting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : defaultFormat === "pdf" ? (
          <FileText className="h-4 w-4" />
        ) : (
          <Image className="h-4 w-4" />
        )}
        {showLabel && <span>{isExporting ? "Exporting..." : label}</span>}
      </Button>
    );
  }

  // Dropdown with format options
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant={variant}
          size={size}
          className={cn("gap-2", className)}
          disabled={disabled || isExporting || !targetRef.current}
          onClick={(e) => e.stopPropagation()}
          aria-label={label}
          title={label}
          {...props}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {showLabel && <span>{isExporting ? "Exporting..." : label}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel>Export Format</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            handleExport("pdf");
          }}
          disabled={isExporting}
          className="cursor-pointer gap-2"
        >
          {exportingFormat === "pdf" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
          Download as PDF
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            handleExport("image");
          }}
          disabled={isExporting}
          className="cursor-pointer gap-2"
        >
          {exportingFormat === "image" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Image className="h-4 w-4" />
          )}
          {canShareImages ? "Share as Image" : "Download as Image"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Compact export button (icon only, single format)
 */
export function ExportAnalysisButtonCompact(
  props: Omit<ExportAnalysisButtonProps, "showLabel" | "showFormatOptions">
) {
  return (
    <ExportAnalysisButton {...props} showLabel={false} showFormatOptions={false} />
  );
}

/**
 * Export button with label and format dropdown
 */
export function ExportAnalysisButtonWithOptions(
  props: Omit<ExportAnalysisButtonProps, "showLabel" | "showFormatOptions">
) {
  return (
    <ExportAnalysisButton {...props} showLabel showFormatOptions />
  );
}

export default ExportAnalysisButton;
